import { collection, doc, query, setDoc, where } from 'firebase/firestore';
import { db } from 'shared/services/firebase';
import { getQueryOnce } from 'shared/services/firestore';

/**
 * Creation of the account_review that pairs 1:1 with every expert account.
 *
 * Ported from aligned's `AccountReviewService.createAccountReview`, which
 * `UserService.createNewUserAccount` calls right after the account doc is
 * written. Lives in `shared/` (not `features/expert/`) because its caller
 * `createNewUserAccount` is a shared signup primitive, and `shared/` may not
 * depend on `features/`. The onboarding-completion enqueue that moves this doc
 * to `pendingReview` lives in `domains/experts/data/accountReview`.
 */

const ACCOUNT_REVIEWS = 'account_reviews';

export interface NewAccountReviewInput {
  expertId: string;
  firebaseId: string;
  expertEmail: string;
}

async function accountReviewExists(expertId: string): Promise<string | null> {
  const rows = await getQueryOnce(
    query(collection(db, ACCOUNT_REVIEWS), where('expertId', '==', expertId)),
    (id) => id,
  );
  return rows[0] ?? null;
}

/**
 * Creates the expert's account review at the initial `onboarding` status,
 * unless one already exists.
 *
 * Idempotent (matching aligned, and coexisting with the cloud-function fallback
 * that also creates these): returns the existing review's id when present.
 * The full field set with defaults mirrors the Flutter `AccountReview` model so
 * the operations review reader sees a complete document.
 */
export async function createAccountReviewIfAbsent(input: NewAccountReviewInput): Promise<string> {
  const existingId = await accountReviewExists(input.expertId);
  if (existingId) return existingId;

  const ref = doc(collection(db, ACCOUNT_REVIEWS));
  const now = new Date().toISOString();
  await setDoc(ref, {
    id: ref.id,
    expertId: input.expertId,
    firebaseId: input.firebaseId,
    expertEmail: input.expertEmail,
    status: 'onboarding',
    priorityScore: 0,
    priorityOverride: false,
    currentReviewerId: null,
    currentReviewerAssignedAt: null,
    reviewCount: 0,
    reviewHistory: [],
    escalated: false,
    escalatedAt: null,
    escalatedBy: null,
    hasMissingPoolAssignments: false,
    createdAt: now,
    updatedAt: now,
    queuedAt: null,
  });
  return ref.id;
}
